import api from '../utils/api';
import {
    acceptTermsResponseSchema,
    checkSumsubCompleteResponseSchema,
    completeAccountResponseSchema,
    imageVerifyResponseSchema,
    imIdAuthTokenResponseSchema,
    parseRegistrationResponse,
    readTermsResponseSchema,
    resendOtpPayloadSchema,
    resendOtpResponseSchema,
    verifyEmailResponseSchema,
    verifyOtpPayloadSchema,
    verifyOtpResponseSchema,
    verifyPartnerResponseSchema,
    verifyPhoneResponseSchema,
    verifyReadTermsResponseSchema,
} from './registrationSchemas';
import type { z } from 'zod';

async function request<T extends z.ZodTypeAny>(
    promise: Promise<unknown>,
    schema: T,
    context: string
): Promise<z.infer<T>> {
    const data = await promise;
    return parseRegistrationResponse(schema, data, context);
}

export function acceptTerms(body: Record<string, unknown>, recaptchaToken: string) {
    return request(
        api.post('/account/term', body, {
            headers: { recaptcha: recaptchaToken },
        }),
        acceptTermsResponseSchema,
        'acceptTerms'
    );
}

/** PUT /account/:id — legacy flow ignores response body; only HTTP success matters. */
export function updateAccount(accountId: string, payload: Record<string, unknown>) {
    return api.put(`/account/${accountId}`, payload);
}

export function verifyOtp(accountId: string, payload: unknown) {
    const parsed = verifyOtpPayloadSchema.parse(payload);
    return request(
        api.post(`/account/verify-otp/${accountId}`, parsed),
        verifyOtpResponseSchema,
        'verifyOtp'
    );
}

export function resendOtp(accountId: string, payload: unknown) {
    const parsed = resendOtpPayloadSchema.parse(payload);
    return request(
        api.post(`/account/resend-otp/${accountId}`, parsed),
        resendOtpResponseSchema,
        'resendOtp'
    );
}

export function verifyEmail(accountId: string, code: string | number) {
    return request(
        api.patch(`/account/email-verify/${accountId}`, { code }),
        verifyEmailResponseSchema,
        'verifyEmail'
    );
}

export function verifyPhone(accountId: string, code: string | number) {
    return request(
        api.patch(`/account/phone-verify/${accountId}`, { code }),
        verifyPhoneResponseSchema,
        'verifyPhone'
    );
}

export function readTerms(accountId: string, code: number) {
    return request(
        api.post(`/account/read-terms/${accountId}`, { code }),
        readTermsResponseSchema,
        'readTerms'
    );
}

export function verifyReadTerms(accountId: string, code: string | number) {
    return request(
        api.post(`/account/verify-read-terms/${accountId}`, { code }),
        verifyReadTermsResponseSchema,
        'verifyReadTerms'
    );
}

export function completeAccount(accountId: string) {
    return request(
        api.post(`/account/complete/${accountId}`),
        completeAccountResponseSchema,
        'completeAccount'
    );
}

export function requestImageVerify(accountId: string) {
    return request(
        api.post(`/account/image-verify/${accountId}`, {}),
        imageVerifyResponseSchema,
        'requestImageVerify'
    );
}

export function checkSumsubComplete(accountId: string, applicantId: string) {
    return request(
        api.post(`/account/check-complete/${accountId}`, { applicantId }),
        checkSumsubCompleteResponseSchema,
        'checkSumsubComplete'
    );
}

export function verifyPartnerAccount(accountId: string | number) {
    return request(
        api.post('/account/verify/partner', { accountId }),
        verifyPartnerResponseSchema,
        'verifyPartnerAccount'
    );
}

export function requestImIdAuthToken(accountId: string, phoneNumber: string) {
    return request(
        api.get('/imid/request-authaas-token', {
            params: { accountId, phoneNumber },
        }),
        imIdAuthTokenResponseSchema,
        'requestImIdAuthToken'
    );
}

export type RemindUsernamePayload = {
    email: string;
    ssn: string;
    language: string;
};

/** POST /account/remind-username — emails MB login + account number to the verified address. */
export function remindUsername(payload: RemindUsernamePayload): Promise<unknown> {
    return api.post('/account/remind-username', payload);
}
